<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="visible"
    width="500px"
    @close="handleClose"
  >
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      label-width="100px"
      label-position="right"
    >
      <!-- 小组创建时显示所属团队信息 -->
      <div v-if="type === 'group' && parentTeam" class="parent-team-info">
        <div class="info-label">所属团队：</div>
        <div class="team-info">
          <span
            class="team-color"
            :style="{ backgroundColor: parentTeam.color }"
          ></span>
          <span class="team-name">{{ parentTeam.name }}</span>
          <span class="team-kind">({{ parentTeam.kind }})</span>
        </div>
      </div>
      <el-form-item label="名称" prop="name">
        <el-input v-model="form.name" :placeholder="namePlacehoder" />
      </el-form-item>
      <el-form-item label="类型" prop="kind" v-if="type === 'team'">
        <el-select
          v-model="form.kind"
          placeholder="请选择类型"
          style="width: 100%"
        >
          <el-option label="一般" value="normal" />
          <el-option label="紫队" value="purple" />
          <el-option label="金队" value="golden" />
        </el-select>
      </el-form-item>
      <el-form-item label="颜色" prop="color" v-if="type === 'team'">
        <el-color-picker
          v-model="form.color"
          format="hex"
          :predefine="predefineColors"
        />
      </el-form-item>
      <el-form-item label="负责人" prop="leader_id">
        <el-select
          v-model="form.leader_id"
          placeholder="请选择负责人"
          filterable
          clearable
          style="width: 100%"
        >
          <el-option
            v-for="user in users"
            :key="user.id"
            :label="user.screen_name"
            :value="user.id"
          />
        </el-select>
      </el-form-item>

      <!-- 小组特有字段 -->
      <template v-if="type === 'group'">
        <el-form-item label="跟单员" prop="merchandiser_id">
          <el-select
            v-model="form.merchandiser_id"
            placeholder="请选择跟单员"
            filterable
            clearable
            style="width: 100%"
          >
            <el-option
              v-for="user in users"
              :key="user.id"
              :label="user.screen_name"
              :value="user.id"
            />
          </el-select>
        </el-form-item>
      </template>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleSubmit" :loading="loading">确定</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import { request } from 'beans-admin-plugin'

@Component
export default class TeamFormDialog extends Vue {
  @Prop({ type: Boolean, default: false }) value
  @Prop({ type: String, default: '创建团队' }) title
  @Prop({ type: Object, default: () => ({}) }) data
  @Prop({ type: String, default: 'team' }) type // 'team' or 'group'
  @Prop({ type: [String, Number], default: null }) parentId // 父级ID，用于小组
  @Prop({ type: Object, default: null }) parentTeam // 父级团队信息，用于小组
  @Prop({ type: Array, default: () => [] }) users // 用户列表

  visible = false
  loading = false

  form = {
    name: '',
    kind: 'normal',
    color: '#409EFF',
    leader_id: '',
    merchandiser_id: '',
    parent_id: null
  }

  // 预定义颜色选项 - 团队管理常用色彩
  predefineColors = [
    // 🔴 红色系 - 热情、活力、紧急
    '#FF4D4F',  // 珊瑚红
    '#F5222D',  // 标准红
    '#CF1322',  // 深红色
    '#A8071A',  // 暗红色

    // 🟠 橙色系 - 温暖、创新、友好
    '#FF7A45',  // 橙红色
    '#FA541C',  // 标准橙
    '#D4380D',  // 深橙色
    '#AD2102',  // 暗橙色

    // 🟡 黄色系 - 明亮、乐观、警示
    '#FFC53D',  // 金黄色
    '#FAAD14',  // 标准黄
    '#D48806',  // 深黄色
    '#AD6800',  // 暗黄色

    // 🟢 绿色系 - 成长、和谐、成功
    '#73D13D',  // 亮绿色
    '#52C41A',  // 标准绿
    '#389E0D',  // 深绿色
    '#237804',  // 暗绿色

    // 🔵 蓝色系 - 专业、信任、稳定
    '#40A9FF',  // 亮蓝色
    '#1890FF',  // 标准蓝
    '#096DD9',  // 深蓝色
    '#0050B3',  // 暗蓝色

    // 🟣 紫色系 - 高贵、神秘、创意
    '#B37FEB',  // 亮紫色
    '#722ED1',  // 标准紫
    '#531DAB',  // 深紫色
    '#391085',  // 暗紫色

    // 🩷 品红系 - 活泼、时尚、个性
    '#F759AB',  // 亮品红
    '#EB2F96',  // 标准品红
    '#C41D7F',  // 深品红色
    '#9E1068',  // 暗品红色

    // 🔵 青色系 - 清新、冷静、平衡
    '#36CFC9',  // 亮青色
    '#13C2C2',  // 标准青
    '#08979C',  // 深青色
    '#006D75',  // 暗青色

    // ⚫ 中性色系 - 经典、简约、通用
    '#8C8C8C',  // 中灰色
    '#595959',  // 深灰色
    '#434343',  // 暗灰色
    '#262626'   // 深黑色
  ]

  get namePlacehoder() {
    return `请输入${this.type === 'team' ? '团队' : '小组'}名称`;
  }

  get rules() {
    const baseRules = {
      name: [
        { required: true, message: `请输入${this.type === 'team' ? '团队' : '小组'}名称`, trigger: 'blur' }
      ],
      leader_id: [
        { required: true, message: '请选择负责人', trigger: 'change' }
      ]
    }

    // 只有团队需要类型和颜色的校验
    if (this.type === 'team') {
      baseRules.kind = [
        { required: true, message: '请选择类型', trigger: 'change' }
      ]
      baseRules.color = [
        { required: true, message: '请选择颜色', trigger: 'change' }
      ]
    }

    // 小组需要跟单员校验
    if (this.type === 'group') {
      baseRules.merchandiser_id = [
        { required: true, message: '请选择跟单员', trigger: 'change' }
      ]
    }

    return baseRules
  }

  get dialogTitle() {
    return this.title || (this.type === 'team' ? '创建团队' : '创建小组')
  }

  @Watch('value')
  onValueChange(val) {
    this.visible = val
    if (val && this.data && Object.keys(this.data).length > 0) {
      this.form = { ...this.data }
    } else if (val) {
      this.resetForm()
    }
  }

  @Watch('parentId')
  onParentIdChange(val) {
    this.form.parent_id = val
  }

  @Watch('visible')
  onVisibleChange(val) {
    this.$emit('input', val)
    if (val) {
      // 弹窗打开时处理数据填充
      this.fillFormData()
    }
  }

  fillFormData() {
    if (this.data && Object.keys(this.data).length > 0) {
      // 编辑模式：处理数据格式，特别是关联对象转换为ID
      this.form = {
        ...this.data,
        leader_id: this.data.leader ? this.data.leader.id : this.data.leader_id,
        merchandiser_id: this.data.merchandiser ? this.data.merchandiser.id : this.data.merchandiser_id,
        parent_id: this.parentId || this.data.parent_id
      }
    } else {
      // 创建模式：重置表单并设置默认值
      this.resetForm()
    }
  }

  resetForm() {
    this.form = {
      name: '',
      kind: 'normal',
      color: '#409EFF',
      leader_id: '',
      merchandiser_id: '',
      parent_id: this.parentId
    }
    this.$nextTick(() => {
      if (this.$refs.form) {
        this.$refs.form.clearValidate()
      }
    })
  }

  handleClose() {
    this.visible = false
    this.resetForm()
  }

  async handleSubmit() {
    try {
      await this.$refs.form.validate()
      this.loading = true

      const isEdit = this.data && this.data.id
      const payload = {
        ...this.form,
        // 确保parent_id正确设置
        parent_id: this.parentId || this.form.parent_id
      }

      let result = {};
      let eventType = '';

      if (isEdit) {
        // 编辑模式：更新现有团队/小组
        result = await this.$autoLoading(request.put(`/teams/${this.data.id}`, payload))
        eventType = this.type === 'team' ? 'team-updated' : 'group-updated'
        this.$message.success(`${this.type === 'team' ? '团队' : '小组'}更新成功`)
      } else {
        // 创建模式：创建新团队/小组
        result = await this.$autoLoading(request.post('/teams', payload))
        eventType = this.type === 'team' ? 'team-created' : 'group-created'
        this.$message.success(`${this.type === 'team' ? '团队' : '小组'}创建成功`)
      }

      // 发射对应的事件，传递结果数据和操作类型
      this.$emit(eventType, {
        type: this.type,
        data: result,
        isEdit,
        originalData: this.data,
        parentId: this.parentId
      })

      this.handleClose()
    } catch (error) {
      this.$message.error(`${this.type === 'team' ? '团队' : '小组'}${this.data && this.data.id ? '更新' : '创建'}失败，请重试`)
    } finally {
      this.loading = false
    }
  }
}
</script>

<style lang="scss" scoped>
.dialog-footer {
  text-align: right;
}

.el-color-picker {
  vertical-align: middle;
}

.parent-team-info {
  background: #f5f7fa;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 20px;
  border-left: 4px solid #409EFF;

  .info-label {
    font-size: 12px;
    color: #909399;
    margin-bottom: 6px;
  }

  .team-info {
    display: flex;
    align-items: center;

    .team-color {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      margin-right: 8px;
      border: 1px solid #e4e7ed;
    }

    .team-name {
      font-weight: 500;
      color: #303133;
      margin-right: 6px;
    }

    .team-kind {
      font-size: 12px;
      color: #909399;
    }
  }
}

.form-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
}
</style>
